import { useNavigate } from 'react-router-dom';
import { usePresentation } from '../hooks/usePresentation.js';
import ToolHeader from '../components/ToolHeader.jsx';
import './Logistics.css';

/**
 * 01 · Logistics
 * Get the details down — room, people, A/V.
 *
 * All form fields are auto-saved to localStorage via usePresentation.
 * No save buttons; the "Saved" pill flashes on every change.
 *
 * As of the four-step refactor, the old "Pages of Content" step is
 * folded in here as a lightweight, read-only content-budget readout
 * (see CONTENT BUDGET below). It derives from the talk duration the
 * user already enters, plus a single speaking-pace control. It is
 * deliberately NOT a second form — just a derived figure that nudges
 * the user toward a realistic amount of content for their slot.
 */

const AV_OPTIONS = [
  { key: 'flipcharts', label: 'Flipcharts', hasCount: true },
  { key: 'projector',  label: 'Projector & screen' },
  { key: 'markers',    label: 'Markers' },
  { key: 'tape',       label: 'Tape' },
  { key: 'mic',        label: 'Mic' },
  { key: 'speakers',   label: 'Speakers' },
];

// ── CONTENT BUDGET ───────────────────────────────────────────────────
// Same maths the old Pages-of-Content estimator used, so the number a
// returning user sees doesn't jump:
//
//     pages = (duration_minutes × wpm) ÷ 500 words-per-page
//
// The only new input is the speaking pace. "Standard" is 150 wpm — the
// exact rate the old tool used — so the default reproduces today's
// figure for anyone who hadn't set a separate Q&A time. Measured /
// Brisk are a ±20% nudge for slower / faster speakers.
const WORDS_PER_PAGE = 500;
const PACE_TIERS = [
  { key: 'measured', label: 'Measured', wpm: 120 },
  { key: 'standard', label: 'Standard', wpm: 150 },
  { key: 'brisk',    label: 'Brisk',    wpm: 180 },
];
const paceWpm = (key) =>
  PACE_TIERS.find((t) => t.key === key)?.wpm ?? 150;

/**
 * Estimate the "pages of content" a slot can hold. Pure + exported so
 * later steps can show the same number as a gentle hint without
 * recomputing the maths differently. Returns null when there's no
 * usable duration yet.
 */
export function estimateContentPages(durationMinutes, paceKey = 'standard') {
  const mins = parseFloat(durationMinutes);
  if (!Number.isFinite(mins) || mins <= 0) return null;
  const words = mins * paceWpm(paceKey);
  return { words, pages: words / WORDS_PER_PAGE };
}

export default function Logistics() {
  const { data, updateTool, clearTool, savedFlash } = usePresentation();
  const navigate = useNavigate();
  const l = data.logistics ?? {};
  const av = l.av ?? {};

  // Speaking pace for the content-budget readout. Persisted so it
  // survives reloads and is available to later steps; defaults to the
  // old tool's 150 wpm ("Standard").
  const pace = l.pace ?? 'standard';

  // Update a single logistics field
  const setField = (key, value) => updateTool('logistics', { [key]: value });

  // Update an A/V checklist field (nested under logistics.av)
  const setAv = (key, value) =>
    updateTool('logistics', { av: { ...av, [key]: value } });

  const setPace = (next) => updateTool('logistics', { pace: next });

  // Derived content budget — recomputed on render, never a stored form.
  const budget = estimateContentPages(l.duration, pace);

  function handleClear() {
    if (confirm('Clear every Logistics field? This can\'t be undone.')) {
      clearTool('logistics');
    }
  }

  function handleCopy() {
    const lines = [
      'LOGISTICS',
      l.title     ? `Title: ${l.title}`          : null,
      l.eventName ? `Event: ${l.eventName}`     : null,
      l.date      ? `Date: ${l.date}`            : null,
      l.time      ? `Time: ${l.time}`            : null,
      l.duration  ? `Duration: ${l.duration} min` : null,
      budget      ? `Content budget: ~${budget.pages.toFixed(1)} pages (${PACE_TIERS.find((t) => t.key === pace)?.label} pace)` : null,
      '',
      l.profile      ? `Audience: ${l.profile}`         : null,
      l.participants ? `# people: ${l.participants}`    : null,
      l.catering     ? `Catering: ${l.catering}`        : null,
      '',
      l.location  ? `Location: ${l.location}`           : null,
      l.room      ? `Room: ${l.room}`                   : null,
      l.roomSetup ? `Setup: ${l.roomSetup}`             : null,
      l.setupNotes ? `Setup notes: ${l.setupNotes}`     : null,
      '',
      'A/V: ' + AV_OPTIONS
        .filter(o => av[o.key])
        .map(o => o.hasCount && av.flipchartsCount ? `${o.label} (×${av.flipchartsCount})` : o.label)
        .join(', '),
      av.notes ? `A/V notes: ${av.notes}` : null,
    ].filter(Boolean).join('\n');

    navigator.clipboard?.writeText(lines).then(
      () => alert('Copied to clipboard.'),
      () => alert('Could not copy — your browser may not support clipboard access.')
    );
  }

  return (
    <div className="logistics">
      <ToolHeader num="01" title="Logistics" savedFlash={savedFlash} />

      <div className="logistics-body">

        {/* ── Presentation ── */}
        <section className="lg-section">
          <h2 className="lg-section-title">Presentation</h2>

          <Field label="Presentation name (your internal label)">
            <input
              type="text"
              value={l.eventName || ''}
              onChange={(e) => setField('eventName', e.target.value)}
              placeholder="e.g. Q2 new supervisor orientation"
            />
          </Field>

          <Field label="Presentation title (the headline your audience sees)">
            <input
              type="text"
              value={l.title || ''}
              onChange={(e) => setField('title', e.target.value)}
              placeholder="e.g. Most injuries happen in the first 90 days"
            />
          </Field>

          <div className="lg-row">
            <Field label="Date">
              <input type="date" value={l.date || ''} onChange={(e) => setField('date', e.target.value)} />
            </Field>
            <Field label="Start time">
              <input type="time" value={l.time || ''} onChange={(e) => setField('time', e.target.value)} />
            </Field>
            <Field label="Min">
              <input
                type="number"
                inputMode="numeric"
                min="0"
                value={l.duration || ''}
                onChange={(e) => setField('duration', e.target.value)}
                placeholder="0"
              />
            </Field>
          </div>

          {/* ── Content budget (folded in from the old Pages step) ──
              A derived readout, not a form. Reproduces the old
              estimator's maths from the duration above + one pace
              control, so the figure is consistent with what users
              saw before. */}
          <div className="lg-budget">
            <div className="lg-budget-readout">
              <span className="eyebrow">Content this slot can hold</span>
              {budget ? (
                <span className="lg-budget-figure">
                  ≈ {budget.pages.toFixed(1)}
                  <span className="lg-budget-unit"> {budget.pages.toFixed(1) === '1.0' ? 'page' : 'pages'}</span>
                  <span className="lg-budget-words"> · {Math.round(budget.words).toLocaleString('en-CA')} words</span>
                </span>
              ) : (
                <span className="lg-budget-empty">Add a duration above to estimate</span>
              )}
            </div>
            <div className="lg-budget-pace" role="group" aria-label="Speaking pace">
              {PACE_TIERS.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  className={`lg-budget-pace-btn ${pace === t.key ? 'is-on' : ''}`}
                  onClick={() => setPace(t.key)}
                  aria-pressed={pace === t.key}
                  title={`${t.label} delivery — ${t.wpm} words per minute`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
          {budget && (
            <p className="lg-budget-note">
              A planning ballpark at {paceWpm(pace)} wpm — not a word-for-word script.
              Pick the slice of content that fits, then shape it in the next steps.
            </p>
          )}
        </section>

        {/* ── Participants ── */}
        <section className="lg-section">
          <h2 className="lg-section-title">Participants</h2>

          <Field label="Participant profile">
            <input
              type="text"
              value={l.profile || ''}
              onChange={(e) => setField('profile', e.target.value)}
              placeholder="Who will be in the room?"
            />
          </Field>

          <div className="lg-row">
            <Field label="# people">
              <input
                type="number"
                inputMode="numeric"
                min="0"
                value={l.participants || ''}
                onChange={(e) => setField('participants', e.target.value)}
                placeholder="0"
              />
            </Field>
            <Field label="Catering" wide>
              <input
                type="text"
                value={l.catering || ''}
                onChange={(e) => setField('catering', e.target.value)}
                placeholder="e.g. Coffee + pastries"
              />
            </Field>
          </div>
        </section>

        {/* ── Venue ── */}
        <section className="lg-section">
          <h2 className="lg-section-title">Venue</h2>

          <Field label="Location / address">
            <input
              type="text"
              value={l.location || ''}
              onChange={(e) => setField('location', e.target.value)}
              placeholder="Street, city"
            />
          </Field>

          <Field label="Room name">
            <input
              type="text"
              value={l.room || ''}
              onChange={(e) => setField('room', e.target.value)}
              placeholder="e.g. Sycamore Room"
            />
          </Field>

          <Field label="Room setup">
            <input
              type="text"
              value={l.roomSetup || ''}
              onChange={(e) => setField('roomSetup', e.target.value)}
              placeholder="e.g. U-shape, 20 seats"
            />
          </Field>

          <Field label="Setup notes">
            <textarea
              value={l.setupNotes || ''}
              onChange={(e) => setField('setupNotes', e.target.value)}
              placeholder="Anything else worth remembering…"
              rows={3}
            />
          </Field>
        </section>

        {/* ── A/V ── */}
        <section className="lg-section">
          <h2 className="lg-section-title">A/V required</h2>

          <div className="lg-av-grid">
            {AV_OPTIONS.map((opt) => (
              <button
                key={opt.key}
                type="button"
                className={`lg-av-pill ${av[opt.key] ? 'is-on' : ''}`}
                onClick={() => setAv(opt.key, !av[opt.key])}
              >
                {opt.label}
                {opt.hasCount && av.flipcharts && av.flipchartsCount ? (
                  <span className="lg-av-count">×{av.flipchartsCount}</span>
                ) : null}
              </button>
            ))}
          </div>

          {av.flipcharts && (
            <Field label="How many flipcharts?">
              <input
                type="number"
                inputMode="numeric"
                min="1"
                value={av.flipchartsCount || ''}
                onChange={(e) => setAv('flipchartsCount', e.target.value)}
                placeholder="e.g. 2"
              />
            </Field>
          )}

          <Field label="A/V notes">
            <textarea
              value={av.notes || ''}
              onChange={(e) => setAv('notes', e.target.value)}
              placeholder="Cables? Adapters? Anything tricky?"
              rows={3}
            />
          </Field>
        </section>

        {/* ── Actions ── */}
        <div className="lg-actions">
          <button type="button" className="btn-text" onClick={() => window.print()}>
            Print / PDF
          </button>
          <button type="button" className="btn-text" onClick={handleCopy}>
            Copy as text
          </button>
          <button type="button" className="btn-text danger" onClick={handleClear}>
            Clear
          </button>
        </div>

        <button
          type="button"
          className="btn btn-primary lg-continue"
          onClick={() => navigate('/tools/objective')}
        >
          Continue to your Objective →
        </button>

      </div>
    </div>
  );
}

/* ─── Field — label + child input wrapper ─── */
function Field({ label, children, wide = false }) {
  return (
    <label className={`lg-field ${wide ? 'is-wide' : ''}`}>
      <span className="eyebrow">{label}</span>
      {children}
    </label>
  );
}
